import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import { yenInc, jbaDispatchExTax } from '../../../../utils/pricing';

// Navy chapter band (same grammar as home/page.tsx NavyBand). Band = divider (#31) / no repeated heading right after a band (#33).
function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

// ===== Butler detail (EN) — full modernization (Implementer A scope). English only, no Japanese chars. =====
// Design base: about/overview + home. Facts from working-tree EN/JA butler + JA hub pricing. No fabrication.

export default function EnButlerPage() {
  useEffect(() => {}, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'Butler Services', href: '/en/butler-services/' },
    { label: 'Butler' }
  ];

  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[38px] py-[15px] text-sm tracking-[0.18em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[38px] py-[15px] text-sm tracking-[0.18em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const features = [
    { en: 'Authentic', t: 'Authentic Butler Experience', d: 'Experience genuine butler service impossible with shorter engagements. With a butler constantly at your side, enjoy immediate support and elegant moments in a refined atmosphere.' },
    { en: 'Customized', t: 'Fully Customizable', d: 'Service design completely adapted to your daily schedule and lifestyle—business-focused, household-focused, or guest-oriented, as you wish.' },
    { en: 'For VIP', t: 'VIP & Affluent Client Service', d: 'Our butlers possess the specialized skills affluent clients require: advanced privacy consideration, confidential information handling, and VIP guest reception.' },
    { en: 'Continuity', t: 'Relationship Through Continuity', d: 'Regular use lets butlers deeply understand your preferences and habits, enabling increasingly personalized, high-quality service.' },
  ];

  const scenes = [
    { t: 'Important Business Days', d: 'Perfect for days with crucial meetings or presentations. Your butler handles breakfast, grooming support, document organization, lunch arrangements, and guest reception, so you focus entirely on business.' },
    { t: 'VIP Guest Entertainment', d: 'When hosting VIP guests, your butler manages venue preparation, meal arrangements and service, beverages, conversation facilitation, and cleanup—delivering prestigious hospitality.' },
    { t: 'Special Events & Parties', d: 'For all-day occasions—birthdays, anniversaries, corporate events—your butler provides comprehensive support from morning preparation through evening cleanup.' },
    { t: 'Recovery Periods & Busy Days', d: 'During recovery or unwell periods, your butler supports meals, medication management, appointment accompaniment, and housework. On packed days, every aspect of life is covered so you are never rushed.' },
  ];

  const schedule = [
    { t: 'A Business-Focused Day', rows: [['8:00', 'Departure prep, breakfast'], ['10:00', 'Guest reception, beverages'], ['12:00', 'Lunch prep & cleanup'], ['13:00', 'Meeting support, email sorting'], ['15:00', 'Errands, dinner prep'], ['18:00', 'Cleanup, next-day prep']] },
    { t: 'A Home-Party Day', rows: [['10:00', 'Venue prep, shopping'], ['11:00', 'Cooking, table setting'], ['13:00', 'Guest reception begins'], ['14:00', 'Serving food & beverages'], ['17:00', 'Dessert & coffee service'], ['19:00', 'Cleanup & full teardown']] },
  ];

  const serviceGroups = [
    { t: 'Meals', d: 'Preparing and serving breakfast, lunch, and dinner; grocery shopping; dishwashing; table setting; nutrition-conscious menu suggestions.' },
    { t: 'Housework', d: 'Cleaning (living room, bedroom, bath, etc.), laundry and ironing, organizing, bed making, watering houseplants.' },
    { t: 'Business Support', d: 'Document sorting, guest reception, meeting room setup, beverage service, mail sorting, schedule management assistance.' },
    { t: 'Personal Support', d: 'Shopping errands, dry-cleaning drop-off and pickup, appointment accompaniment, pet care, child pickup support.' },
    { t: 'Events & Entertaining', d: 'Venue setup, food service, guest reception, event management, cleanup.' },
    { t: 'Other', d: 'Advice drawing on butler expertise, etiquette consultation for ceremonial occasions, gift selection support.' },
  ];

  const faqs = [
    ['How does Butler differ from the 3-hour Footman?', 'The full 8 hours enable more comprehensive, in-depth service, with flexible response paced to your habits across the day.'],
    ['Can I specify the hours?', 'Yes. Choose any 8 hours between 8:00 and 21:00.'],
    ['Can the same butler come weekly?', 'Yes, regular contracts are available. Continuity enables increasingly personalized, high-quality service.'],
    ['Can multiple butlers attend?', 'Yes. For large events, we also arrange multiple butlers.'],
    ['Can you cook as well?', 'Yes, from everyday home cooking to hospitality cuisine. Please consult us in advance for special dishes.'],
  ];

  const related = [
    { name: 'Footman', cad: 'Spot use from 3 hours', price: yenInc(jbaDispatchExTax.footmanBase), unit: '~ (incl. tax)', href: '/en/services/footman/' },
    { name: 'Second Steward', cad: '20 days per month, continuous', price: yenInc(jbaDispatchExTax.secondStewardMonthly), unit: '~ (incl. tax)', href: '/en/services/second-steward/' },
    { name: 'House Steward', cad: '24 hours, three-butler rotation', price: yenInc(jbaDispatchExTax.houseStewardMonthly), unit: ' (incl. tax)', href: '/en/services/house-steward/' },
  ];

  return (
    <Layout>
      <title>Butler | 8-Hour Dedicated Butler Service & Pricing | Japan Butler Association</title>
      <meta name="description" content={`THE BUTLER's Butler plan offers an 8-hour experience with a dedicated professional butler—supporting your entire day from morning prep to evening cleanup. From ¥${yenInc(jbaDispatchExTax.butlerBase)} (incl. tax, 8 hours), with free butler selection.`} />
      <link rel="canonical" href="https://butler-association.jp/en/services/butler" />

      <section className="relative bg-[#1a2942] overflow-hidden min-h-[500px] lg:min-h-[580px] flex items-center">
        <img src="/images/gen2/service-en-butler-hero-full-day-prep-01.jpg" alt="Butler service. A Japanese senior butler preparing full-day dining support in a private residence" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[700px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Plan II — Butler</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(27px,3.2vw,42px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">Eight hours a day,</span>
              <span className="block">with a dedicated butler</span>
            </h1>
            <div className="space-y-5">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                Butler is our core plan, in which a dedicated butler supports a full 8 hours. From morning preparation to evening care, an experienced butler supports your life throughout the day.
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                With this much time, your butler grasps your pace and habits to deliver more attentive, anticipatory hospitality.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Breadcrumb items={breadcrumbs} />

      <NavyBand no="I" ja="About This Service" en="Overview" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S1 Overview */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              Ample time makes <span className="text-[#a38e65]">a finer day</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
              <div className="space-y-5">
                <p>Butler is our core plan for clients who desire an authentic butler experience or need comprehensive support through a special day.</p>
                <p>A full 8 hours lets you experience the essence of butler service: anticipatory care, flexible response, and dignified presence.</p>
              </div>
              <div className="space-y-5">
                <p>This service embodies more than 130 years of butler education cultivated by the Japan Butler Association.</p>
                <p>Our butlers are certified professionals who passed rigorous examinations and accumulated experience—understanding your lifestyle and preferences to provide optimal, personalized service rather than simply performing tasks.</p>
              </div>
              <p>The 8-hour timeframe lets butlers grasp your pace and habits for more attentive care. From morning wake-up through breakfast, daytime support, and dinner preparation to cleanup, we provide comprehensive service aligned with your daily rhythm.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8 mt-10">
              {features.map((f) => (
                <div key={f.t} className="border-t border-[#e2ddd2] pt-6">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{f.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2.5">{f.t}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.d}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="II" ja="Usage Scenes" en="Scenes" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S2 Scenes */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Where Butler delivers the most value
            </h2>
            <div className="grid lg:grid-cols-[1fr_340px] gap-8 lg:gap-12 items-start">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                {scenes.map((s) => (
                  <div key={s.t} className="border-l-2 border-[#a38e65]/40 pl-4">
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{s.t}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
                  </div>
                ))}
              </div>
              <img src="/images/gen2/service-en-butler-detail-table-service-01.jpg" alt="A Japanese butler presenting a plated course for attentive table-side Butler service" className="w-full h-64 lg:h-[360px] object-cover border border-[#a38e65]/30 order-first lg:order-none" />
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="III" ja="A Day With Your Butler" en="Daily Rhythm" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S3 A Day With Butler */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              A sample schedule
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
              These are examples. Choose any 8 hours between 8:00 and 21:00, and freely customize the content to your needs.
            </p>
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
              {schedule.map((sc) => (
                <div key={sc.t} className="bg-[#f7f5f0] p-7 sm:p-8">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-5">{sc.t}</h3>
                  <ol className="space-y-3.5">
                    {sc.rows.map(([time, act]) => (
                      <li key={time} className="flex gap-5 font-['Noto_Serif_JP',serif] text-[15px] leading-[1.7]">
                        <span className="w-14 shrink-0 text-[#a38e65] font-['Playfair_Display',serif]">{time}</span>
                        <span className="text-[#333]">{act}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="IV" ja="What We Provide" en="What We Do" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S4 What We Do */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Comprehensive service in 8 hours
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
              {serviceGroups.map((g) => (
                <div key={g.t} className="border-t border-[#e2ddd2] pt-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2.5">{g.t}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{g.d}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="V" ja="Pricing" en="Fees" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S5 Pricing */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Clear and transparent pricing
            </h2>
            <div className="grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-12 items-start">
              <div className="bg-[#1a2942] p-8 text-center">
                <div className="font-['Noto_Serif_JP',serif] text-[42px] text-[#f8f6f0] leading-none">&yen;{yenInc(jbaDispatchExTax.butlerBase)}</div>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] mt-3">incl. tax &middot; 8 hours</p>
              </div>
              <div>
                <dl className="border-t-2 border-[#1a2942]">
                  {[
                    ['Base Rate', `8 hours: ¥${yenInc(jbaDispatchExTax.butlerBase)} (incl. tax)`],
                    ['Service Hours', '8 hours between 8:00 and 21:00'],
                    ['Extension', `¥${yenInc(jbaDispatchExTax.butlerExtension)}/hour (incl. tax)`],
                    ['Butler Selection', 'Free'],
                    ['Transportation', 'Actual expenses'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-6 border-b border-[#e2ddd2] py-4">
                      <dt className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em]">{k}</dt>
                      <dd className="font-['Noto_Serif_JP',serif] text-[17px] text-[#1a2942] text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85] mt-6">
                  <span className="text-[#8f7a56]">Payment:</span> bank transfer or credit card. Cancellation: free up to 7 days prior / 30% at 3–6 days / 100% day before or same day.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="VI" ja="Frequently Asked Questions" en="FAQ" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S6 FAQ */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Frequently asked questions
            </h2>
            <div className="max-w-3xl divide-y divide-[#e2ddd2] border-t border-[#e2ddd2]">
              {faqs.map(([q, a]) => (
                <div key={q} className="py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2.5">Q. {q}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{a}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="VII" ja="Other Plans" en="More Options" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S7 Other Plans */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              Compare the plans
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
              For a single trial choose Footman; for ongoing support, Second Steward; to entrust the whole estate, House Steward.
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map((r) => (
                <a key={r.name} href={r.href} className="group block border border-[#e2ddd2] hover:border-[#a38e65] transition-colors duration-300 p-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2 group-hover:text-[#a38e65] transition-colors">{r.name}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] mb-4">{r.cad}</p>
                  <div className="font-['Noto_Serif_JP',serif] text-[22px] text-[#1a2942]">&yen;{r.price}<span className="text-sm text-[#8f7a56] ml-1">{r.unit}</span></div>
                </a>
              ))}
            </div>
            <div className="mt-8"><a href="/en/butler-services/" className={BTN08_ON_LIGHT}>Back to all services</a></div>
          </section>
        </div>
      </div>

      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S8 CTA */}
          <section>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <h2 className="jba-display font-medium text-[#f8f6f0] text-xl sm:text-2xl lg:text-[28px] leading-[1.5] mb-4">Experience premium Butler service</h2>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                Let us know your wishes by phone or form. Our specialists will listen closely and propose the ideal service plan and pricing.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <a href="/en/contact" className={BTN08_ON_NAVY}>Contact Us</a>
                <span className="font-['Noto_Serif_JP',serif] text-[#e7e2d8] text-[15px]">Phone: 03-6824-4086 (Weekdays 10:00-18:00)</span>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
